// Job Lists page — each Job List is a Wishlist under the Teifi Wishlists API
// (assets/wishlist-api.js). Rows are rendered from the API, not mock markup.
if (!customElements.get('job-lists-wrapper')) {
  customElements.define(
    'job-lists-wrapper',
    class JobListsWrapper extends HTMLElement {
      static DEBOUNCE_TIME = 300;
      static PAGE_SIZE = 10;
      static THUMBS_VISIBLE = 4;
      // 'items' (item count) isn't a sortable field on the API — anything not
      // listed here is sorted client-side against the currently loaded rows.
      static API_SORT_FIELDS = { name: 'name', updated: 'updatedAt' };

      connectedCallback() {
        this.rowsContainer = this.querySelector('[data-job-lists-rows]');
        this.searchInput = this.querySelector('[data-job-lists-search]');
        this.searchWrapper = this.querySelector('.job-lists__search');
        this.sortSelect = this.querySelector('[data-job-lists-sort-select]');
        this.sortButtons = this.querySelectorAll('[data-job-lists-sort]');
        this.selectAllCheckbox = this.querySelector('[data-job-lists-select-all]');
        this.noResultsMessage = this.querySelector('[data-job-lists-no-results]');
        this.emptyMessage = this.querySelector('[data-job-lists-empty-message]');
        this.countBadge = this.querySelector('[data-job-lists-count]');
        this.rowTemplate = this.querySelector('[data-job-list-row-template]');
        this.thumbPlaceholderTemplate = this.querySelector('[data-job-list-thumb-placeholder]');
        this.loadMoreWrapper = this.querySelector('[data-job-lists-load-more]');
        this.loadMoreBtn = this.querySelector('[data-job-lists-load-more-btn]');
        this.ownerName = this.dataset.ownerName || '';
        this.createModal = document.getElementById(this.dataset.createModalId);
        this.shareModal = document.getElementById(this.dataset.shareModalId);
        this.createButton = this.querySelector('.job-lists__create-btn');
        // Set while the create modal is open in "edit" mode: { id, wishlist }.
        this.editing = null;
        this.sharingId = null;
        // `?id=<list id>` switches the page to the detail view (see the inline script in main-job-lists).
        this.detailId = new URLSearchParams(window.location.search).get('id');
        this.detailView = this.querySelector('[data-job-list-detail]');

        this.page = 1;
        this.search = '';
        this.sort = null;
        this.currentSort = { field: null, direction: 'asc' };

        this.bindEvents();
        if (this.detailId) this.initDetail();
        else this.load();
      }

      bindEvents() {
        if (this.searchInput) {
          this.searchInput.addEventListener(
            'input',
            FoxTheme.utils.debounce(() => {
              this.search = this.searchInput.value.trim();
              this.page = 1;
              this.load();
            }, JobListsWrapper.DEBOUNCE_TIME)
          );
        }

        if (this.searchWrapper) {
          const searchIcon = this.searchWrapper.querySelector('svg');
          searchIcon?.addEventListener('click', () => {
            this.searchWrapper.classList.toggle('is-active');
            if (this.searchWrapper.classList.contains('is-active')) this.searchInput.focus();
          });
        }

        this.sortButtons.forEach((button) => {
          button.addEventListener('click', () => this.handleSortButtonClick(button));
        });

        this.sortSelect?.addEventListener('change', (event) => this.handleSortSelectChange(event));

        this.selectAllCheckbox?.addEventListener('change', (event) => this.handleSelectAll(event));

        this.addEventListener('click', (event) => this.handleRowAction(event));

        this.loadMoreBtn?.addEventListener('click', () => {
          this.page += 1;
          this.load({ append: true });
        });

        // The modal's own aria-controls listener opens it; this only resets it to "create".
        this.createButton?.addEventListener('click', () => this.setCreateModalMode(null));

        this.handleSubmitBound = (event) => this.handleSubmit(event);
        document.addEventListener('submit', this.handleSubmitBound);
      }

      disconnectedCallback() {
        if (this.handleSubmitBound) {
          document.removeEventListener('submit', this.handleSubmitBound);
          this.handleSubmitBound = null;
        }
      }

      getRows() {
        return Array.from(this.rowsContainer?.querySelectorAll('[data-job-list-row]') || []);
      }

      async load({ append = false } = {}) {
        const params = { page: this.page, limit: JobListsWrapper.PAGE_SIZE };
        if (this.search) params.search = this.search;
        if (this.sort) params.sort = this.sort;

        // Only the latest request may touch the rows — a slower earlier search/sort/page is dropped.
        const requestId = (this.loadRequestId = (this.loadRequestId ?? 0) + 1);

        if (append) {
          if (this.loadMoreBtn) this.loadMoreBtn.disabled = true;
        } else {
          this.rowsContainer.replaceChildren(this.buildMessage('Loading…'));
          this.loadMoreWrapper?.classList.add('hidden');
        }

        try {
          const result = await WishlistAPI.getLists(params);
          const wishlists = result?.items ?? [];
          const enriched = await Promise.all(
            wishlists.map(async (wishlist) => ({
              ...wishlist,
              items: await enrichWishlistItems(wishlist.items ?? []),
            }))
          );
          if (requestId !== this.loadRequestId) return;

          if (!append) this.rowsContainer.replaceChildren();
          for (const wishlist of enriched) {
            const row = this.buildRow(wishlist);
            if (row) this.rowsContainer.appendChild(row);
          }

          // Client-side sorts (item count) only cover loaded rows — reapply after every load/append.
          const { field, direction } = this.currentSort;
          if (field && !JobListsWrapper.API_SORT_FIELDS[field]) this.applySort(field, direction);

          this.totalItems = result?.totalItems ?? this.getRows().length;
          this.updateCount(this.totalItems);
          this.toggleEmptyState();
          this.toggleLoadMore(result);
        } catch (err) {
          if (requestId !== this.loadRequestId) return;
          console.error('[JobListsWrapper] load failed:', err);
          if (append) {
            this.page -= 1;
          } else {
            this.rowsContainer.replaceChildren(
              this.buildMessage(FoxTheme.jobListsStrings?.loadError ?? 'Failed to load.')
            );
          }
        } finally {
          if (this.loadMoreBtn && requestId === this.loadRequestId) this.loadMoreBtn.disabled = false;
        }
      }

      buildMessage(text) {
        const p = document.createElement('p');
        p.className = 'py-10 text-center opacity-60';
        p.textContent = text;
        return p;
      }

      buildRow(wishlist) {
        const node = this.rowTemplate?.content.cloneNode(true).firstElementChild;
        if (!(node instanceof HTMLElement)) return null;

        const id = idFromGid(wishlist.metaobjectGid);
        const name = wishlist.name || '';
        const items = wishlist.items ?? [];

        node.dataset.id = id;
        this.fillListFields(node, wishlist);

        const nameEl = node.querySelector('[data-field="name"]');
        if (nameEl) nameEl.href = this.getDetailUrl(id);

        node.querySelectorAll('[data-field="items-count"]').forEach((el) => {
          el.textContent = items.length;
        });

        const checkbox = node.querySelector('[data-job-list-select]');
        if (checkbox) {
          const label = (FoxTheme.jobListsStrings?.selectRow ?? 'Select [name]').replace('[name]', name);
          checkbox.setAttribute('aria-label', label);
        }

        this.renderThumbs(node.querySelector('[data-field="thumbs"]'), items);

        return node;
      }

      getDetailUrl(id) {
        return `${window.location.pathname}?id=${encodeURIComponent(id)}`;
      }

      // Name / description / "Last updated … by X" — shared by the list rows and the detail header.
      fillListFields(node, wishlist) {
        const updatedAt = wishlist.updatedAt ? new Date(wishlist.updatedAt).toLocaleDateString() : '';
        const owner = this.getLastEditorName(wishlist);

        const nameEl = node.querySelector('[data-field="name"]');
        if (nameEl) nameEl.textContent = wishlist.name || '';

        const descEl = node.querySelector('[data-field="description"]');
        if (descEl) descEl.textContent = wishlist.description || '';

        node.querySelectorAll('[data-field="updated-date"], [data-field="updated-date-mobile"]').forEach((el) => {
          el.textContent = updatedAt;
        });
        node.querySelectorAll('[data-field="owner"], [data-field="owner-mobile"]').forEach((el) => {
          el.textContent = owner;
        });
      }

      // "Last updated … by X": activityLogs are newest first. Customers without a first/last
      // name only have an email, so fall back to it, then to the logged-in customer.
      getLastEditorName(wishlist) {
        const log = wishlist.activityLogs?.[0];
        const name = [log?.firstName, log?.lastName].filter(Boolean).join(' ');
        return name || log?.email || this.ownerName;
      }

      renderThumbs(container, items) {
        if (!container) return;
        container.replaceChildren();

        const visible = items.slice(0, JobListsWrapper.THUMBS_VISIBLE);
        for (const item of visible) {
          const span = document.createElement('span');
          span.className = 'job-list-row__thumb';
          if (item.imageUrl) {
            const img = document.createElement('img');
            img.src = item.imageUrl;
            img.alt = '';
            img.loading = 'lazy';
            span.appendChild(img);
          } else if (this.thumbPlaceholderTemplate) {
            span.appendChild(this.thumbPlaceholderTemplate.content.cloneNode(true));
          }
          container.appendChild(span);
        }

        const extra = items.length - visible.length;
        if (extra > 0) {
          const more = document.createElement('span');
          more.className = 'job-list-row__thumb job-list-row__thumb--more';
          more.textContent = `+${extra}`;
          container.appendChild(more);
        }
      }

      handleSortButtonClick(button) {
        const field = button.dataset.jobListsSort;
        this.toggleSortDirection(field);
        this.applySort(field, this.currentSort.direction);
        this.updateSortButtonState(button);
      }

      handleSortSelectChange(event) {
        const [field, direction] = event.target.value.split('-');
        this.currentSort = { field, direction };
        this.applySort(field, direction);
      }

      toggleSortDirection(field) {
        if (this.currentSort.field === field) {
          this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
        } else {
          this.currentSort = { field, direction: 'asc' };
        }
      }

      updateSortButtonState(activeButton) {
        this.sortButtons.forEach((button) => button.classList.toggle('is-active', button === activeButton));
      }

      applySort(field, direction) {
        const apiField = JobListsWrapper.API_SORT_FIELDS[field];
        if (apiField) {
          this.sort = `${apiField}:${direction}`;
          this.page = 1;
          this.load();
          return;
        }

        if (!this.rowsContainer) return;
        const rows = this.getRows();
        rows.sort((a, b) => {
          const valueA = parseInt(a.querySelector('[data-field="items-count"]')?.textContent, 10) || 0;
          const valueB = parseInt(b.querySelector('[data-field="items-count"]')?.textContent, 10) || 0;
          return direction === 'asc' ? valueA - valueB : valueB - valueA;
        });
        rows.forEach((row) => this.rowsContainer.appendChild(row));
      }

      handleSelectAll(event) {
        const checked = event.target.checked;
        this.getRows().forEach((row) => {
          const checkbox = row.querySelector('[data-job-list-select]');
          if (checkbox) checkbox.checked = checked;
        });
      }

      handleRowAction(event) {
        const actionButton = event.target.closest('[data-action]');
        if (!actionButton) {
          // A click on a list row's empty space opens that list, same as its name link.
          const row = event.target.closest('[data-job-list-row]');
          if (row && !event.target.closest('a, button, input, label, summary, details')) {
            window.location.href = this.getDetailUrl(row.dataset.id);
          }
          return;
        }

        const row = actionButton.closest('[data-job-list-row], [data-job-list-detail]');
        if (!row) return;

        const action = actionButton.dataset.action;
        const id = row.dataset.id;

        if (action === 'delete') {
          this.deleteRow(id, row);
        } else if (action === 'copy' && this.detailId) {
          // Detail view: copy the items into an existing or a new list (Figma "Copy" modal).
          document.querySelector('job-list-add-form')?.open(this.getDetailRows(), actionButton);
        } else if (action === 'copy') {
          this.duplicateRow(id);
        } else if (action === 'add-to-cart') {
          this.addRowToCart(id, actionButton);
        } else if (action === 'edit') {
          this.openEditModal(id, actionButton);
        } else if (action === 'share') {
          this.openShareModal(id, actionButton);
        }

        const menu = actionButton.closest('.job-list-row__menu');
        if (menu) menu.removeAttribute('open');
      }

      async deleteRow(id, row) {
        if (!window.confirm(FoxTheme.jobListsStrings?.deleteConfirm ?? 'Delete this job list?')) return;

        try {
          await WishlistAPI.deleteList(id);
          if (this.detailId) {
            window.location.href = window.location.pathname;
            return;
          }
          row.remove();
          this.totalItems = Math.max(0, (this.totalItems ?? 1) - 1);
          this.updateCount(this.totalItems);
          this.toggleEmptyState();
        } catch (err) {
          console.error('[JobListsWrapper] delete failed:', err);
          window.alert(FoxTheme.jobListsStrings?.loadError ?? 'Failed to load.');
        }
      }

      async duplicateRow(id) {
        try {
          const wishlist = await WishlistAPI.getList(id);
          const suffix = FoxTheme.jobListsStrings?.copyNameSuffix ?? ' (Copy)';
          await WishlistAPI.createList(`${wishlist.name}${suffix}`, wishlist.description ?? '', wishlist.items ?? []);
          this.page = 1;
          await this.load();
        } catch (err) {
          console.error('[JobListsWrapper] copy failed:', err);
          window.alert(FoxTheme.jobListsStrings?.saveError ?? 'Failed to save.');
        }
      }

      async addRowToCart(id, btn) {
        btn.disabled = true;
        try {
          // Detail view: add the rows as currently edited (plus items that didn't render),
          // not the last saved copy of the list.
          let items;
          if (this.detailId) {
            await this.detailReady;
            items = this.collectDetailItems();
          } else {
            items = (await WishlistAPI.getList(id))?.items ?? [];
          }
          const result = await addItemsToShopifyCart(items);
          if (result) await publishCartRefresh(result.sections);
        } catch (err) {
          console.error('[JobListsWrapper] add to cart failed:', err);
          window.alert(FoxTheme.jobListsStrings?.addToCartError ?? 'Failed to add to cart.');
        } finally {
          btn.disabled = false;
        }
      }

      handleSubmit(event) {
        const shareForm = event.target.closest('.job-list-share__form');
        if (shareForm && this.shareModal?.contains(shareForm)) {
          this.handleShareSubmit(event, shareForm);
        } else {
          this.handleCreateSubmit(event);
        }
      }

      // `editing` is { id, wishlist } for "Edit Job List", null for "Create New Job List".
      setCreateModalMode(editing) {
        this.editing = editing;
        if (!this.createModal) return;

        const isEdit = !!editing;
        this.createModal.querySelectorAll('[data-mode-create]').forEach((el) => (el.hidden = isEdit));
        this.createModal.querySelectorAll('[data-mode-edit]').forEach((el) => (el.hidden = !isEdit));

        const form = this.createModal.querySelector('.job-list-create__form');
        if (!form) return;
        form.reset();
        if (isEdit) {
          form.elements.list_name.value = editing.wishlist.name ?? '';
          form.elements.list_description.value = editing.wishlist.description ?? '';
        }
      }

      async openEditModal(id, btn) {
        if (!this.createModal) return;
        btn.disabled = true;
        try {
          const wishlist = await WishlistAPI.getList(id);
          this.setCreateModalMode({ id, wishlist });
          this.createModal.show(btn);
        } catch (err) {
          console.error('[JobListsWrapper] edit load failed:', err);
          window.alert(FoxTheme.jobListsStrings?.loadError ?? 'Failed to load.');
        } finally {
          btn.disabled = false;
        }
      }

      openShareModal(id, btn) {
        if (!this.shareModal) return;
        this.sharingId = id;
        this.shareModal.querySelector('.job-list-share__form')?.reset();
        this.shareModal.show(btn);
      }

      async handleShareSubmit(event, form) {
        event.preventDefault();

        const id = this.sharingId;
        const senderName = form.elements.sender_name.value.trim();
        const message = form.elements.message.value.trim();
        const emails = form.elements.emails.value
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean);
        if (!id || !senderName || !message || !emails.length) return;

        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        try {
          await this.sendCopy(id, emails, senderName, message);
          this.shareModal.hide();
        } catch (err) {
          console.error('[JobListsWrapper] share failed:', err);
          window.alert(err.message || (FoxTheme.jobListsStrings?.saveError ?? 'Failed to save.'));
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      }

      // ponytail: the Wishlists app can't email non-customers, so "Send a copy" turns on
      // the list's public share link and hands off to the shopper's mail client. Swap for a
      // server-side send if the Wishlists app ever exposes one.
      async sendCopy(id, emails, senderName, message) {
        const wishlist = await WishlistAPI.updateShareable(id, true);
        if (!wishlist?.sharedUrl) throw new Error(FoxTheme.jobListsStrings?.saveError ?? 'Failed to save.');

        const subject = (FoxTheme.jobListsStrings?.shareCopySubject ?? '[sender] shared a job list with you: [name]')
          .replace('[sender]', senderName)
          .replace('[name]', wishlist.name ?? '');
        const body = `${message}\n\n${wishlist.sharedUrl}?format=pdf`;
        window.location.href = `mailto:${emails.map(encodeURIComponent).join(',')}?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`;
      }

      async handleCreateSubmit(event) {
        const form = event.target.closest('.job-list-create__form');
        if (!form) return;

        event.preventDefault();

        const nameInput = form.querySelector('[name="list_name"]');
        const descriptionInput = form.querySelector('[name="list_description"]');
        const name = nameInput?.value.trim();
        const description = descriptionInput?.value.trim();

        if (!name || !description) return;

        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        try {
          if (this.editing) {
            const { id, wishlist } = this.editing;
            await WishlistAPI.updateList(id, {
              name,
              handle: wishlist.handle ?? '',
              description,
              items: (wishlist.items ?? []).map(WishlistAPI.toWritableItem),
            });
          } else {
            await WishlistAPI.createList(name, description, []);
          }
          form.reset();
          form.closest('basic-modal')?.hide();
          if (this.detailId) {
            await this.loadDetail();
          } else {
            this.page = 1;
            await this.load();
          }
        } catch (err) {
          console.error('[JobListsWrapper] create failed:', err);
          window.alert(FoxTheme.jobListsStrings?.saveError ?? 'Failed to save.');
        } finally {
          if (submitBtn) submitBtn.disabled = false;
        }
      }

      // ─── Detail view (?id=) ───────────────────────────────────────
      // The list's items are edited with the Quick Order component (search, CSV upload,
      // quantity, remove); every change it reports is saved back to the Job List.
      initDetail() {
        if (!this.detailView) return;
        this.detailView.dataset.id = this.detailId;
        this.quickOrder = this.detailView.querySelector('quick-order-component');
        this.itemSearch = this.detailView.querySelector('[data-job-list-item-search]');
        this.unresolvedItems = [];

        this.addEventListener(
          'quick-order:change',
          FoxTheme.utils.debounce(() => this.saveDetailItems(), JobListsWrapper.DEBOUNCE_TIME)
        );
        this.itemSearch?.addEventListener('input', () => this.filterDetailRows());
        document.addEventListener('job-list:items-added', (event) => {
          if (event.detail?.id === this.detailId) this.loadDetail();
        });

        this.loadDetail();
      }

      getDetailRows() {
        return Array.from(this.quickOrder?.querySelectorAll('[data-product-list] quick-order-item') || []);
      }

      // Kept as a promise so actions that read the rows can wait for them to render.
      loadDetail() {
        this.detailReady = this.fetchDetail();
        return this.detailReady;
      }

      async fetchDetail() {
        try {
          const wishlist = await WishlistAPI.getList(this.detailId);
          this.detail = wishlist;
          this.renderDetailHeader(wishlist);
          await this.renderDetailItems(wishlist.items ?? []);
        } catch (err) {
          console.error('[JobListsWrapper] detail load failed:', err);
          this.detailView.replaceChildren(
            this.buildMessage(FoxTheme.jobListsStrings?.loadError ?? 'Failed to load.')
          );
        }
      }

      renderDetailHeader(wishlist) {
        this.fillListFields(this.detailView, wishlist);
        const descEl = this.detailView.querySelector('[data-field="description"]');
        if (descEl && !wishlist.description) descEl.textContent = FoxTheme.jobListsStrings?.noDescription ?? '';
        descEl?.classList.toggle('is-empty', !wishlist.description);

        // Breadcrumbs: "Home | Job Lists | <list name>" — the page title becomes a link back.
        const last = document.querySelector('.breadcrumbs .breadcrumbs--last');
        if (last && !last.hasAttribute('data-job-list-name')) {
          const link = document.createElement('a');
          link.href = window.location.pathname;
          link.className = 'reversed-link';
          link.textContent = last.textContent;
          const separator = document.createElement('span');
          separator.className = 'breadcrumbs--sep';
          separator.setAttribute('aria-hidden', 'true');
          last.before(link, separator);
          last.setAttribute('data-job-list-name', '');
        }
        if (last) last.textContent = wishlist.name || '';
      }

      async renderDetailItems(items) {
        const rows = await Promise.all(items.map((item) => this.fetchItemRow(item)));
        // Items whose product/variant no longer renders are kept as-is so a save doesn't drop them.
        this.unresolvedItems = items.filter((_, index) => !rows[index]).map(WishlistAPI.toWritableItem);

        const list = this.quickOrder.querySelector('[data-product-list]');
        this.getDetailRows().forEach((row) => row.remove());
        rows.forEach((row, index) => {
          if (!row) return;
          row.querySelector('[data-item-quantity]').value = String(Math.max(1, items[index].quantity || 1));
          const checkbox = row.querySelector('[data-item-checkbox]');
          if (checkbox) checkbox.checked = true;
          list.appendChild(row);
        });

        // Rendering the saved items is not an edit — don't echo it back to the API.
        this.savedItemsKey = JSON.stringify(this.collectDetailItems());
        this.quickOrder.updateSummary();
        this.filterDetailRows();
      }

      // Renders one saved item with the Quick Order row markup via the Section Rendering API.
      async fetchItemRow(item) {
        const variantId = idFromGid(item.variantGid);
        if (!item.handle || !variantId) return null;

        try {
          const url = `${window.Shopify?.routes?.root ?? '/'}products/${encodeURIComponent(item.handle)}?variant=${variantId}&section_id=job-list-item`;
          const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!res.ok) return null;
          const row = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('quick-order-item');
          // An unknown ?variant= falls back to the first variant; don't let that swap the saved variant.
          return row?.dataset.variantId === variantId ? row : null;
        } catch (err) {
          console.error('[JobListsWrapper] item render failed:', err);
          return null;
        }
      }

      collectDetailItems() {
        return this.getDetailRows().map((row) => WishlistAPI.itemFromRow(row)).concat(this.unresolvedItems);
      }

      // Saves run one at a time; each queued save reads the rows when it starts, so the
      // latest edits always land last and an older request can't overwrite them.
      saveDetailItems() {
        this.savingDetail = (this.savingDetail ?? Promise.resolve())
          .then(() => this.persistDetailItems())
          .catch((err) => console.error('[JobListsWrapper] queued save failed:', err));
        return this.savingDetail;
      }

      async persistDetailItems() {
        if (!this.detail) return;
        const items = this.collectDetailItems();
        const key = JSON.stringify(items);
        if (key === this.savedItemsKey) return;
        this.savedItemsKey = key;

        try {
          await WishlistAPI.updateList(this.detailId, {
            name: this.detail.name,
            handle: this.detail.handle ?? '',
            description: this.detail.description ?? '',
            items,
          });
        } catch (err) {
          this.savedItemsKey = null;
          console.error('[JobListsWrapper] save items failed:', err);
          window.alert(FoxTheme.jobListsStrings?.saveError ?? 'Failed to save.');
        }
      }

      filterDetailRows() {
        const term = this.itemSearch?.value.trim().toLowerCase() ?? '';
        this.getDetailRows().forEach((row) => {
          row.hidden = !!term && !row.textContent.toLowerCase().includes(term);
        });
      }

      updateCount(count) {
        if (this.countBadge) this.countBadge.textContent = `(${count})`;
      }

      toggleLoadMore(result) {
        const hasMore = (result?.page ?? 1) < (result?.totalPages ?? 1);
        this.loadMoreWrapper?.classList.toggle('hidden', !hasMore);
      }

      // Zero rows reads two different ways: no lists at all vs a search that
      // matched nothing. Either way the table (search bar, column headers)
      // stays visible — only the message below the rows changes.
      toggleEmptyState() {
        const hasRows = this.getRows().length > 0;
        const isFiltering = !!this.search;

        this.noResultsMessage?.classList.toggle('hidden', hasRows || !isFiltering);
        this.emptyMessage?.classList.toggle('hidden', hasRows || isFiltering);
      }
    }
  );
}

// "Add Item to Job List" modal (snippets/job-list-add-modal.liquid): saves the given
// <quick-order-item> rows into an existing Job List or a new one.
if (!customElements.get('job-list-add-form')) {
  customElements.define(
    'job-list-add-form',
    class JobListAddForm extends HTMLElement {
      static LISTS_LIMIT = 100;

      connectedCallback() {
        // <basic-modal> moves itself to <body> on show (and back on hide), which reconnects
        // this element — bind once or every open adds another submit listener.
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        this.modal = this.closest('basic-modal');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.items = [];

        this.form.addEventListener('change', () => this.update());
        this.form.addEventListener('input', () => this.update());
        this.form.addEventListener('submit', (event) => this.submit(event));
      }

      async open(rows, opener) {
        this.items = rows.map((row) => WishlistAPI.itemFromRow(row));
        if (!this.items.length) return;

        this.form.reset();
        this.update();
        this.modal?.show(opener);

        const select = this.form.elements.list_id;
        select.length = 1;
        try {
          // ponytail: first 100 lists only; page through getLists() if buyers outgrow that.
          const result = await WishlistAPI.getLists({ limit: JobListAddForm.LISTS_LIMIT });
          for (const list of result?.items ?? []) {
            select.add(new Option(list.name, idFromGid(list.metaobjectGid)));
          }
        } catch (err) {
          console.error('[JobListAddForm] load lists failed:', err);
        }
      }

      update() {
        const { target, list_id, list_name } = this.form.elements;
        this.querySelectorAll('[data-target-field]').forEach((field) => {
          field.hidden = field.dataset.targetField !== target.value;
        });
        list_name.required = target.value === 'new';
        this.submitButton.disabled =
          target.value === 'existing' ? !list_id.value : target.value === 'new' ? !list_name.value.trim() : true;
      }

      async submit(event) {
        event.preventDefault();
        const { target, list_id, list_name } = this.form.elements;
        this.submitButton.disabled = true;

        try {
          let id = list_id.value;
          if (target.value === 'existing') {
            await WishlistAPI.addItems(id, this.items);
          } else {
            const created = await WishlistAPI.createList(list_name.value.trim(), '', this.items);
            id = idFromGid(created?.metaobjectGid);
          }
          this.modal?.hide();
          this.dispatchEvent(new CustomEvent('job-list:items-added', { bubbles: true, detail: { id } }));
        } catch (err) {
          console.error('[JobListAddForm] save failed:', err);
          window.alert(FoxTheme.jobListsStrings?.saveError ?? 'Failed to save.');
        } finally {
          this.update();
        }
      }
    }
  );
}
